import React, { useState } from 'react'
import { Button, Skeleton, Stack, useTheme } from '@mui/material'
import { useFetchAuthenticatedUser } from 'hooks/use-authenticated-user'
import { useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, toastAtom } from 'state'
import { User, UserUpdate, OrgUserRole } from 'api/model'
import {
  usersDeletePicture,
  usersUpdateAuthenticated,
  usersUploadPicture,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { isFieldEmpty, isPhoneNumberValid } from 'utils'
import { useActiveOrganization } from 'hooks/use-organization'
import EditablePicture from 'components/EditablePicture'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import ReadOnlyKeyPair from 'components/ReadOnlyKeyPair'

const MyProfile = () => {
  const isMobile = useCheckMobileScreen()

  const [activeOrganization] = useActiveOrganization()
  const { user } = useFetchAuthenticatedUser()

  const setToast = useSetRecoilState(toastAtom)

  const setProfile = useSetRecoilState(authenticatedUserAtom)

  const onPicturePicked: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!user) {
      return
    }

    if (!event.target.files) {
      return
    }

    const file = event.target.files[0]

    try {
      const result = await usersUploadPicture(file, {
        headers: { 'Content-Type': 'application/octet-stream' },
      })

      setProfile((user) => {
        if (!user) {
          return null
        }

        return {
          ...user,
          picture: result.data.picture,
        }
      })
    } catch (Error) {
      setToast({ title: 'Failed to upload profile photo', severity: 'error' })
    }
  }

  const onPictureRemoved = async () => {
    if (!user) {
      return
    }

    try {
      await usersDeletePicture()
      setProfile((user) => {
        if (!user) {
          return null
        }

        const { picture, ...updatedUser } = user
        return updatedUser
      })
    } catch (error) {
      setToast({ title: 'Profile photo removal failed', severity: 'error' })
    }
  }

  const renderFields = () => {
    if (!user) {
      return <LoadingMyProfile />
    }

    return (
      <EditProfile
        user={user}
        onFinishedEditing={(didUpdate) => {
          if (didUpdate) {
            setToast({ title: 'Profile updated successfully', severity: 'success' })
          }
        }}
        activeOrganization={activeOrganization}
      />
    )
  }

  const renderPicture = () => {
    if (!user) {
      return
    }

    return (
      <EditablePicture
        onPicturePicked={onPicturePicked}
        onPictureRemoved={onPictureRemoved}
        src={user?.picture}
        changeLabel="profile photo"
        alt="Profile photo"
        style={{ borderRadius: '9999px', height: '160px', width: '160px' }}
      />
    )
  }

  return (
    <Stack
      sx={{
        width: isMobile ? '100%' : '376px',
      }}>
      <Stack
        width={isMobile ? '100%' : '376px'}
        mb={4}
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}>
        {renderPicture()}
      </Stack>

      {renderFields()}
    </Stack>
  )
}

const EditProfile = ({
  user,
  onFinishedEditing,
  activeOrganization,
}: {
  user: User
  onFinishedEditing: (didUpdate: boolean) => void
  activeOrganization: string | null
}) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  // if the org user role is member, we want to disable some of the profile inputs
  const isMember = user.profiles.find((up) => up.organization.id === activeOrganization)?.role === OrgUserRole.member

  const setProfile = useSetRecoilState(authenticatedUserAtom)

  const [phoneNumber, setPhoneNumber] = useState<string>(user.phone_number ?? '')
  const onPhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value)
    setHasChanges(true)
  }

  const [linkedInUrl, setLinkedInUrl] = useState<string>(user.linkedin_url ?? '')
  const onLinkedInUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLinkedInUrl(event.target.value)
    setHasChanges(true)
  }

  const [twitterUrl, setTwitterUrl] = useState<string>(user.twitter_url ?? '')
  const onTwitterUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTwitterUrl(event.target.value)
    setHasChanges(true)
  }

  const [alternateEmail, setAlternateEmail] = useState<string>(user.alternate_email ?? '')
  const onAlternateEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAlternateEmail(event.target.value)
    setHasChanges(true)
  }

  const [title, setTitle] = useState<string>(user.title ?? '')
  const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value)
    setHasChanges(true)
  }

  const setToast = useSetRecoilState(toastAtom)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const onFormSubmit = async () => {
    setIsSubmitting(true)
    setSubmitCount(submitCount + 1)

    if (isFieldEmpty(title)) {
      setIsSubmitting(false)
      return
    }

    try {
      const update: UserUpdate = {
        phone_number: phoneNumber,
        title,
        linkedin_url: linkedInUrl,
        twitter_url: twitterUrl,
        alternate_email: alternateEmail,
      }
      const result = await usersUpdateAuthenticated(update)
      setProfile(result.data)

      onFinishedEditing(true)
      setSubmitCount(0)
    } catch (Error) {
      setToast({ title: 'Profile update failed', severity: 'error' })
      onFinishedEditing(false)
    } finally {
      setIsSubmitting(false)
      setHasChanges(false)
    }
  }

  const [hasChanges, setHasChanges] = useState<boolean>(false)

  return (
    <Stack sx={{ width: isMobile ? '100%' : '376px' }}>
      <Stack spacing={4} sx={{ width: isMobile ? '100%' : '376px' }} mt={2} mb={isMobile ? 0 : 4}>
        <ReadOnlyKeyPair name="Email" value={user.email} />

        <TextFieldWithDarkMode
          className="has-label"
          required={true}
          theme={theme}
          isDarkBackdrop={true}
          label="Headline"
          placeholder="e.g. Software Engineer at Startup"
          value={title}
          onChange={onTitleChange}
          disabled={isMember}
          error={submitCount > 0 && isFieldEmpty(title)}
          helperText={submitCount > 0 && isFieldEmpty(title) && 'Headline is required'}
        />

        <TextFieldWithDarkMode
          className="has-label"
          theme={theme}
          isDarkBackdrop={true}
          label="Phone"
          placeholder="Enter number"
          value={phoneNumber}
          onChange={onPhoneNumberChange}
          disabled={isSubmitting || isMember}
          error={!isPhoneNumberValid(phoneNumber, true)}
          helperText={!isPhoneNumberValid(phoneNumber, true) && 'Invalid Phone'}
        />

        <TextFieldWithDarkMode
          className="has-label"
          theme={theme}
          isDarkBackdrop={true}
          label="LinkedIn"
          placeholder="Enter LinkedIn profile url"
          value={linkedInUrl}
          onChange={onLinkedInUrlChange}
          disabled={isSubmitting}
        />

        <TextFieldWithDarkMode
          className="has-label"
          theme={theme}
          isDarkBackdrop={true}
          label="Twitter"
          placeholder="Enter Twitter profile url"
          value={twitterUrl}
          onChange={onTwitterUrlChange}
          disabled={isSubmitting}
        />

        <TextFieldWithDarkMode
          className="has-label"
          theme={theme}
          isDarkBackdrop={true}
          label="Alternate email"
          placeholder="Enter an alternate email"
          value={alternateEmail}
          onChange={onAlternateEmailChange}
          disabled={isSubmitting}
        />
        {hasChanges && (
          <Button
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            onClick={onFormSubmit}
            sx={{
              width: 'fit-content',
              alignSelf: 'flex-end',
            }}>
            Save changes
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

const LoadingMyProfile = () => {
  return (
    <Stack spacing={4} sx={{ width: '376px' }}>
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
    </Stack>
  )
}

export default MyProfile
